// Admin › Klinik Detay › Klinik Bilgileri — WhatsApp bağlantı durumu kartı.
// Görüntüleme admin-clinics:read (sayfa zaten bunu ister); askıya alma / kaldırma ve
// şablonları yenileme admin-clinics:whatsapp. Kaynak: POST /api/admin/whatsapp/list (klinik süzülür).

import React, { useState } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { WhatsApp as WhatsAppIcon, PauseCircleOutline as PauseIcon, PlayCircleOutline as PlayIcon } from "@mui/icons-material";
import { ActionButton, InfoRow, formatPhoneDisplay } from "../../../components/atoms";
import { PromptDialog, SectionCard } from "../../../components/molecules";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO, CARD_PAD } from "../../../styles/uiConstants";
import { formatDateTime, phoneMismatch } from "../../../constants/whatsapp";
import WhatsAppTemplateList from "./WhatsAppTemplateList";
import {
  useAdminSyncWhatsAppTemplatesMutation,
  useAdminSuspendWhatsAppMutation,
  useAdminUnsuspendWhatsAppMutation,
  useAdminWhatsAppListQuery,
} from "../../../store/whatsappApi";

const STATUS = {
  connected: { label: "Bağlı", tone: "success" },
  suspended: { label: "Askıya alındı", tone: "warning" },
  disconnected: { label: "Bağlantı kesildi", tone: "neutral" },
  not_connected: { label: "Bağlı değil", tone: "neutral" },
};

const accountClinicId = (a) => String(a?.clinicId || a?.clinic?._id || a?.clinic || "");
const valueSx = { fontSize: TYPO.body, color: COLORS.slate800, pt: 0.4 };

// clinicPhone: İletişim ve Adres'teki telefon — bağlantı ancak bu girilmişse açılır.
export default function ClinicWhatsAppCard({ clinicId, token, palette, sx, clinicPhone }) {
  const { showSuccess, showError } = useAlert();
  const canManage = useHasPermission("admin-clinics:whatsapp");
  const { data, isLoading, isError } = useAdminWhatsAppListQuery({ token, clinicId }, { skip: !token || !clinicId });
  const [suspend, { isLoading: suspending }] = useAdminSuspendWhatsAppMutation();
  const [unsuspend, { isLoading: unsuspending }] = useAdminUnsuspendWhatsAppMutation();
  const [syncTemplates, { isLoading: syncing }] = useAdminSyncWhatsAppTemplatesMutation();
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [unsuspendOpen, setUnsuspendOpen] = useState(false);

  const accounts = data?.data?.accounts || [];
  const account = accounts.find((a) => accountClinicId(a) === String(clinicId)) || null;
  const status = account?.status || "not_connected";
  const info = STATUS[status] || STATUS.not_connected;
  const suspended = status === "suspended";

  const handleSuspend = async (reason) => {
    try {
      await suspend({ token, clinicId, reason }).unwrap();
      setSuspendOpen(false);
      showSuccess("WhatsApp bağlantısı askıya alındı");
    } catch (e) {
      showError(e?.data?.message || "Askıya alınamadı");
    }
  };
  const handleUnsuspend = async () => {
    try {
      await unsuspend({ token, clinicId }).unwrap();
      setUnsuspendOpen(false);
      showSuccess("Askı kaldırıldı");
    } catch (e) {
      showError(e?.data?.message || "Askı kaldırılamadı");
    }
  };

  const handleSync = async () => {
    try {
      await syncTemplates({ token, clinicId }).unwrap();
      showSuccess("Şablon durumları güncellendi");
    } catch (e) {
      showError(e?.data?.message || "Şablonlar yenilenemedi");
    }
  };

  // Askıya alma yalnız bir hesap kaydı varken anlamlı
  const action =
    canManage && account && !isLoading ? (
      suspended ? (
        <ActionButton
          variant="confirm"
          compact
          tone="outline"
          palette={palette}
          icon={<PlayIcon sx={{ fontSize: 15 }} />}
          label="Askıyı kaldır"
          onClick={() => setUnsuspendOpen(true)}
        />
      ) : (
        <ActionButton
          variant="delete"
          compact
          tone="soft"
          icon={<PauseIcon sx={{ fontSize: 15 }} />}
          label="Askıya al"
          onClick={() => setSuspendOpen(true)}
        />
      )
    ) : null;

  return (
    <SectionCard
      title="WhatsApp"
      subtitle="Kliniğin kendi WhatsApp Business hattı"
      icon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
      sx={sx}
      action={action}
    >
      {isLoading ? (
        <Box sx={{ px: CARD_PAD, py: 1.5 }}>
          <Skeleton variant="text" width="40%" height={24} />
          <Skeleton variant="text" width="70%" height={24} />
        </Box>
      ) : isError ? (
        <Typography sx={{ px: CARD_PAD, py: 1.5, fontSize: TYPO.body, color: COLORS.slate500 }}>
          WhatsApp durumu şu an alınamıyor.
        </Typography>
      ) : (
        <>
          <InfoRow label="Durum">
            <Box sx={{ pt: 0.25 }}>
              <StatusPill label={info.label} tone={info.tone} palette={palette} />
            </Box>
          </InfoRow>
          {!account || status === "not_connected" || status === "disconnected" ? (
            <InfoRow label="Klinik telefonu" noBorder={!account}>
              <Typography sx={{ ...valueSx, color: clinicPhone ? COLORS.slate800 : COLORS.slate500 }}>
                {clinicPhone
                  ? formatPhoneDisplay(clinicPhone)
                  : "Girilmedi · WhatsApp bağlantısı için İletişim ve Adres'e telefon girin"}
              </Typography>
            </InfoRow>
          ) : null}
          {account ? (
            <InfoRow label="Numara" noBorder={!suspended}>
              <Typography sx={valueSx}>
                {[account.displayPhoneNumber, account.verifiedName].filter(Boolean).join(" · ") || "—"}
              </Typography>
              {phoneMismatch(account.displayPhoneNumber, clinicPhone) ? (
                <Typography sx={{ fontSize: TYPO.label, color: COLORS.errorDark, mt: 0.25 }}>
                  Klinik telefonundan farklı ({formatPhoneDisplay(clinicPhone)}). Bu numarayla yeniden bağlanılmalı.
                </Typography>
              ) : null}
            </InfoRow>
          ) : null}
          {suspended ? (
            <InfoRow label="Askı nedeni" noBorder>
              <Typography sx={valueSx}>
                {account?.suspension?.reason || "—"}
                {account?.suspension?.at ? (
                  <Box component="span" sx={{ color: COLORS.slate500 }}>
                    {" · "}
                    {formatDateTime(account.suspension.at)}
                  </Box>
                ) : null}
              </Typography>
            </InfoRow>
          ) : null}
          {status === "connected" || (account?.templates || []).some((t) => t.status !== "NOT_CREATED") ? (
            <WhatsAppTemplateList
              templates={account?.templates}
              canSync={canManage && (status === "connected" || suspended)}
              onSync={handleSync}
              syncing={syncing}
              palette={palette}
            />
          ) : null}
        </>
      )}

      <PromptDialog
        open={suspendOpen}
        onClose={() => setSuspendOpen(false)}
        onConfirm={handleSuspend}
        title="WhatsApp bağlantısını askıya al"
        message="Askıdayken klinik WhatsApp'tan mesaj gönderemez ve gelen mesajlar panele düşmez. Neden kliniğe gösterilir."
        inputLabel="Neden"
        placeholder="Ör. Meta politikasına aykırı içerik"
        required
        confirmText="Askıya al"
        cancelText="Vazgeç"
        confirmVariant="warning"
        loading={suspending}
        maxLength={500}
      />
      <ConfirmDialog
        open={unsuspendOpen}
        onClose={() => setUnsuspendOpen(false)}
        onConfirm={handleUnsuspend}
        title="Askıyı kaldır"
        message="Klinik WhatsApp hattını yeniden kullanabilir. Bağlantı kopmuşsa klinik kendi panelinden yeniden bağlamalıdır."
        confirmText="Askıyı kaldır"
        cancelText="Vazgeç"
        loading={unsuspending}
      />
    </SectionCard>
  );
}
